import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Renderer2, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap, first, takeUntil, tap, catchError } from 'rxjs/operators';
import { ToastService } from 'ngx-zcs-components';
import { MatStepper, StepperOrientation } from '@angular/material/stepper';

import { FirmwareTestStorageService, InverterFirmwareItem, InverterFirmwareMetadata, LoggerType, LoggerFirmwareItem, SafetyBinaryItem, SafetyCustomItem } from '../firmware-test-storage/firmware-test-storage.service';
import { TestConfigurationService, FirmwareConfigOverrides, ChipData, FirmwareConfig, LoggerOverrideData, SafetyOverrides, SafetyOverrideData } from '../test-configuration/test-configuration.service';
import { FirmwareConfigService } from '../firmware-config/firmware-config.service';
import { SgpInteraction, parseSafetyCustomHeader, buildSafetyOverrideKey } from '../../SGP';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators/blockSizeType.decorator';

// Firmware Effect mapping (only actively used types)
enum FirmwareEffect {
  ARM = 1,           // ARM fotovoltaico
  DSPM = 2,          // DSPM fotovoltaico
  DSPS = 3,          // DSPS fotovoltaico
  BMS = 5,           // Sistema di gestione della batteria (BMS)
  PCU = 6,           // Unità di controllo della potenza (PCU)
  BDU = 7,           // Unità di distribuzione della batteria (BDU)
  AFCI = 10,         // AFCI (Interruzione da arco elettrico)
}

// Firmware Effect labels in Italian
const FirmwareEffectLabels: Record<FirmwareEffect, string> = {
  [FirmwareEffect.ARM]: 'ARM fotovoltaico',
  [FirmwareEffect.DSPM]: 'DSPM fotovoltaico',
  [FirmwareEffect.DSPS]: 'DSPS fotovoltaico',
  [FirmwareEffect.BMS]: 'Sistema di gestione della batteria (BMS)',
  [FirmwareEffect.PCU]: 'Unità di controllo della potenza (PCU)',
  [FirmwareEffect.BDU]: 'Unità di distribuzione della batteria (BDU)',
  [FirmwareEffect.AFCI]: 'AFCI (Interruzione da arco elettrico)',
};

// Mapping from FirmwareEffect to module type for configuration
const FirmwareEffectToModuleType: Record<FirmwareEffect, string> = {
  [FirmwareEffect.ARM]: 'ARM',
  [FirmwareEffect.DSPM]: 'DSPM',
  [FirmwareEffect.DSPS]: 'DSPS',
  [FirmwareEffect.BMS]: 'BATTERY.BMS',
  [FirmwareEffect.PCU]: 'BATTERY.PCU',
  [FirmwareEffect.BDU]: 'BATTERY.BDU',
  [FirmwareEffect.AFCI]: 'AFCI',
};

// Types for module selection
type InverterModuleType = 'ARM' | 'DSPM' | 'DSPS' | 'AFCI' | 'SAFETY';
type BatteryModuleType = 'BATTERY.BMS' | 'BATTERY.PCU' | 'BATTERY.BDU';
type AllModuleTypes = InverterModuleType | BatteryModuleType;

// Pattern config type (can be inverter or logger)
interface PatternConfig {
  DEVICE_MODEL_ID: string;
  ID?: string;
  MODE?: string[];
  [key: string]: any;
}

// Wizard state interface (INVERTER)
interface WizardSelections {
  pattern: string | null;
  patternConfig: PatternConfig | null;
  moduleType: AllModuleTypes | null;
  chipId: string | null;
  originalChipData: ChipData | null;
  selectedFirmware: InverterFirmwareItem | null;
}

// Logger config interface
interface LoggerConfig extends PatternConfig {
  LATEST_SOLARMAN_LOGGER_BY_WEB?: string;
  LATEST_SOLARMAN_LOGGER?: string;
  LINK_SOLARMAN_LOGGER?: string;
}

// Logger wizard state interface
interface LoggerWizardSelections {
  pattern: string | null;
  originalConfig: LoggerConfig | null;
  override: {
    LATEST_SOLARMAN_LOGGER_BY_WEB: string;
    LATEST_SOLARMAN_LOGGER: string;
    LINK_SOLARMAN_LOGGER: string;
  };
}

const labels = {
  // General
  firmwareMode: 'Firmware Mode',
  productionModeActive: 'Production Mode - Using firmware from S3',
  testModeActive: 'Test Mode Active - Using test firmware from Firebase Storage. Test mode is persistent and synchronized across all devices.',
  inverter: 'Inverter',
  logger: 'Logger',
  safetyConfig: 'Safety',

  // Toast messages
  testModeActivated: 'TEST MODE ACTIVE - You are using test firmware!',
  productionModeRestored: 'Production mode restored',
  errorChangingMode: 'Error changing mode',
  selectBinFile: 'Please select a .bin file',
  errorExtractingMetadata: 'Error extracting metadata',
  firmwareUploadedSuccessfully: 'Firmware uploaded successfully!',
  errorUploading: 'Upload error',
  versionAndModuleTypeRequired: 'Version and Module Type are required. The firmware binary does not contain valid metadata.',
  overrideSavedSuccessfully: 'Override saved successfully!',
  errorSavingOverride: 'Error saving override',
  overrideRemoved: 'Override removed!',
  errorRemoving: 'Error removing',
  patternOverrideRemoved: 'Pattern override removed!',
  errorRemovingPattern: 'Error removing pattern',
  allOverridesRemoved: 'All overrides removed!',
  error: 'Error',
  fileAndLoggerTypeRequired: 'File and Logger Type are required',
  loggerFirmwareUploadedSuccessfully: 'Logger firmware uploaded successfully!',
  allFieldsRequired: 'All fields are required',
  loggerOverrideSavedSuccessfully: 'Logger override saved successfully!',
  errorSaving: 'Error saving',

  // Step 1: Upload
  uploadFirmware: 'Upload Firmware',
  uploadInverterTestFirmware: 'Upload Inverter Test Firmware',
  dragDropBinFile: 'Drag & drop your .bin file here',
  orClickToBrowse: 'or click to browse',
  extractedMetadata: 'Extracted Metadata',
  firmwareName: 'Firmware Name',
  version: 'Version',
  chipModel: 'Chip Model',
  firmwareEffect: 'Firmware Effect',
  buildDate: 'Build Date',
  crc32: 'CRC32',
  deviceModel: 'Device Model',
  moduleType: 'Module Type',
  chipId: 'Chip ID',
  versionAutoFilled: 'Version',
  autoFilledFields: 'Auto-filled from firmware (read-only)',
  notes: 'Notes (optional)',
  notesPlaceholder: 'Any notes about this firmware...',
  uploading: 'Uploading',
  uploadFirmwareButton: 'Upload Firmware',
  reset: 'Reset',
  uploadLoggerTestFirmware: 'Upload Logger Test Firmware',
  noMetadataExtractionForLogger: 'No metadata extraction for logger firmware - simple upload',
  dragDropLoggerBinFile: 'Drag & drop your logger .bin file here',
  loggerType: 'Logger Type',
  selectLoggerType: 'Select logger type',
  notesOptional: 'Notes (optional)',
  anyNotes: 'Any notes...',
  uploadLoggerFirmware: 'Upload Logger Firmware',
  inverterTestFirmware: 'Inverter Test Firmware',
  yourUploadedTestFirmware: 'Your uploaded test firmware files',
  noTestFirmwareUploaded: 'No test firmware uploaded yet.',
  loggerTestFirmware: 'Logger Test Firmware',
  yourUploadedLoggerFirmware: 'Your uploaded logger firmware files',
  noLoggerFirmwareUploaded: 'No logger firmware uploaded yet.',

  // Step 2: View Config
  viewConfig: 'View Config',
  inverterFirmwareConfiguration: 'Inverter Firmware Configuration',
  productionConfigurationForInverters: 'Production configuration for inverter devices',
  copy: 'Copy',
  loggerFirmwareConfiguration: 'Logger (Datalogger) Firmware Configuration',
  productionConfigurationForLoggers: 'Production configuration for logger devices',
  safetyFirmwareConfiguration: 'Safety Firmware Configuration',
  productionConfigurationForSafety: 'Production configuration for safety firmware',

  // Step 3: Manage Overrides
  manageOverrides: 'Manage Overrides',
  createInverterOverride: 'Create Inverter Override',
  step1SelectDevicePattern: 'Step 1: Select Device Pattern',
  chooseDevicePattern: 'Choose the device pattern for the override',
  step2SelectModuleType: 'Step 2: Select Module Type',
  pattern: 'Pattern',
  step3SelectChip: 'Step 3: Select Chip',
  module: 'Module',
  currentVersion: 'Current Version',
  step4SelectTestFirmware: 'Step 4: Select Test Firmware',
  chip: 'Chip',
  noCompatibleFirmware: 'No compatible firmware found. Please upload firmware first.',
  uploaded: 'Uploaded',
  step5PreviewAndConfirm: 'Step 5: Preview and Confirm',
  reviewOverrideConfiguration: 'Review your override configuration before saving',
  originalVersion: 'Original Version',
  newVersion: 'New Version',
  firebasePath: 'Firebase Path',
  back: 'Back',
  confirmAndSave: 'Confirm and Save',
  createLoggerOverride: 'Create Logger Override',
  step1SelectLoggerPattern: 'Step 1: Select Logger Pattern',
  chooseLoggerPattern: 'Choose the logger pattern for the override',
  step2ConfigureLoggerOverride: 'Step 2: Configure Logger Override',
  loggerTypeLabel: 'Logger Type',
  latestSolarmanLoggerByWeb: 'LATEST_SOLARMAN_LOGGER_BY_WEB',
  latestSolarmanLogger: 'LATEST_SOLARMAN_LOGGER',
  linkSolarmanLogger: 'LINK_SOLARMAN_LOGGER',
  versionFields: 'Version Fields',
  selectUploadedLoggerFirmware: 'Select uploaded logger firmware',
  next: 'Next',
  step3PreviewAndConfirm: 'Step 3: Preview and Confirm',
  reviewLoggerOverrideConfiguration: 'Review your logger override configuration before saving',
  myActiveInverterOverrides: 'My Active Inverter Overrides',
  manageCurrentFirmwareOverrides: 'Manage your current firmware overrides',
  deletePattern: 'Delete Pattern',
  noActiveInverterOverrides: 'No active inverter overrides.',
  clearAllInverterOverrides: 'Clear All Inverter Overrides',
  myActiveLoggerOverrides: 'My Active Logger Overrides',
  manageCurrentLoggerOverrides: 'Manage your current logger firmware overrides',
  delete: 'Delete',
  noActiveLoggerOverrides: 'No active logger overrides.',

  // Confirm dialogs
  confirmRemoveOverride: 'Remove override for',
  confirmRemoveAllOverrides: 'Remove ALL the override for pattern',
  confirmRemoveAllOverridesQuestion: 'Remove ALL overrides?',
  confirmRemoveLoggerOverride: 'Remove logger override for',
  confirmRemoveSafetyOverride: 'Remove safety override for',

  // Safety labels
  uploadSafetyBinary: 'Upload Safety Binary',
  uploadSafetyCustom: 'Upload Safety Custom',
  safetyBinaryDescription: 'Upload a .bin file containing safety configurations',
  safetyCustomDescription: 'Upload a .txt file with country/region-specific safety override',
  selectDeviceModelFirst: 'Select a device model first',
  dragDropSafetyBin: 'Drag & drop your safety .bin file here',
  dragDropSafetyTxt: 'Drag & drop your safety .txt file here',
  safetyBuildDate: 'Build Date',
  safetyCountryCode: 'Country Code',
  safetyRegionCode: 'Region Code',
  safetyVersion: 'Version',
  safetyOverrideKey: 'Override Key',
  uploadSafetyBinaryButton: 'Upload Safety Binary',
  uploadSafetyCustomButton: 'Upload Safety Custom',
  safetyBinaryTestFiles: 'Safety Binary Test Files',
  yourUploadedSafetyBinaries: 'Your uploaded safety binary files',
  safetyCustomTestFiles: 'Safety Custom Test Files',
  yourUploadedSafetyCustom: 'Your uploaded safety custom files',
  noSafetyBinaryFiles: 'No safety binary files uploaded.',
  noSafetyCustomFiles: 'No safety custom files uploaded.',
  manageSafetyOverrides: 'Manage Safety Overrides',
  manageSafetyOverridesDescription: 'Configure safety overrides for test mode',
  safetyOverrideType: 'Override Type',
  overrideBinary: 'Override Binary',
  overrideCustom: 'Override Custom Files',
  selectSafetyBinary: 'Select Safety Binary',
  selectSafetyCustomFiles: 'Select Custom Files',
  selectFiles: 'Select Files',
  optional: 'optional',
  safetyOverrideSavedSuccessfully: 'Safety override saved successfully!',
  mySafetyOverrides: 'My Active Safety Overrides',
  manageCurrentSafetyOverrides: 'Manage your current safety overrides',
  noActiveSafetyOverrides: 'No active safety overrides.',
  binaryOverride: 'Binary',
  customOverrides: 'Custom Files',
  noBinaryOverride: '(using production)',

  // Module types
  arm: 'ARM',
  dspm: 'DSPM',
  dsps: 'DSPS',
  afci: 'AFCI',
  safety: 'SAFETY',
  batteryBms: 'Battery → BMS',
  batteryPcu: 'Battery → PCU',
  batteryBdu: 'Battery → BDU',
  inverterModules: 'Inverter Modules',
  batteryModules: 'Battery Modules',

  // Device models
  selectDeviceModel: 'Select device model',

  // Filters
  filterByDeviceModel: 'Filter by Device Model',
  allDeviceModels: 'All Device Models',
  noFirmwareForFilter: 'No firmware found for this device model.',
};

@CreateCustomBlockSizeType({
  'breakpoint-768': 768,
  'breakpoint-1024': 1024,
})
@Component({
  selector: 'zcs-test-config-editor',
  templateUrl: './test-config-editor.component.html',
  styleUrls: ['./test-config-editor.component.scss']
})
export class TestConfigEditorComponent implements OnInit, OnDestroy, ICustomBlock {
  @ViewChild('stepper') stepper?: MatStepper;

  private destroy$ = new Subject<void>();

  // ICustomBlock properties
  width?: number;
  currentWidth?: number;

  // Labels for translations
  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  // Mode state
  currentMode: 'production' | 'test' = 'production';

  // Stepper state
  selectedStepIndex = 0;
  stepperOrientation: StepperOrientation = 'horizontal';

  // Update stepper orientation based on window width
  private updateStepperOrientation() {
    const width = this.width ?? window.innerWidth ?? 1200;
    this.stepperOrientation = width < 768 ? 'vertical' : 'horizontal';
  }

  // Firmware mode content for customer-title-content
  get firmwareModeContent(): string {
    if (this.currentMode === 'production') {
      return `✅ ${this.labels.productionModeActive}`;
    } else {
      return `⚠️ ${this.labels.testModeActive}`;
    }
  }

  // Upload form
  uploadForm!: FormGroup;
  selectedFile: File | null = null;
  extractedMetadata: any | null = null;
  uploadProgress = 0;
  isUploading = false;
  extractedFirmwareEffect: number = 0;
  firmwareEffectLabel: string = '';
  moduleTypeFromEffect: string = '';

  // Configuration viewer
  productionConfig$!: Observable<FirmwareConfig>;
  configRefresh$ = new BehaviorSubject<void>(undefined);
  isLoadingConfig = true;

  // My test firmware
  myTestFirmware$!: Observable<InverterFirmwareItem[]>;
  firmwareRefresh$ = new BehaviorSubject<void>(undefined);
  isLoadingFirmware = true;

  // My overrides
  myOverrides$!: Observable<FirmwareConfigOverrides>;
  overridesRefresh$ = new BehaviorSubject<void>(undefined);
  isLoadingOverrides = true;

  // Logger loading states
  isLoadingLoggerFirmware = true;
  isLoadingLoggerOverrides = true;

  // Wizard state
  wizardStep: 'select-pattern' | 'select-module' | 'select-chip' | 'select-firmware' | 'preview' = 'select-pattern';
  wizardSelections: WizardSelections = {
    pattern: null,
    patternConfig: null,
    moduleType: null,
    chipId: null,
    originalChipData: null,
    selectedFirmware: null
  };

  // Available options per step
  availablePatterns$!: Observable<Array<{ pattern: string; id: string }>>;
  availableModules$!: Observable<string[]>;
  availableChips$!: Observable<ChipData[]>;
  compatibleFirmware$!: Observable<InverterFirmwareItem[]>;
  isLoadingCompatibleFirmware = false;

  // Device models from production config (filtered by INVERTER)
  availableDeviceModels$!: Observable<{ value: string; label: string }[]>;

  // Filters for lists
  firmwareListFilterDeviceModel = '';
  configFilterDeviceModel = '';
  wizardFilterDeviceModel = '';

  // Module types for dropdown
  get moduleTypes() {
    return [
      { group: this.labels.inverterModules, options: [
        { value: 'ARM', label: this.labels.arm },
        { value: 'DSPM', label: this.labels.dspm },
        { value: 'DSPS', label: this.labels.dsps },
        { value: 'AFCI', label: this.labels.afci },
        { value: 'SAFETY', label: this.labels.safety }
      ]},
      { group: this.labels.batteryModules, options: [
        { value: 'BATTERY.BMS', label: this.labels.batteryBms },
        { value: 'BATTERY.PCU', label: this.labels.batteryPcu },
        { value: 'BATTERY.BDU', label: this.labels.batteryBdu }
      ]}
    ];
  }

  // ========== LOGGER PROPERTIES ==========

  // Device type selector
  selectedDeviceType: 'INVERTER' | 'LOGGER' | 'SAFETY' = 'INVERTER';

  // Logger types for dropdown
  readonly loggerTypes: LoggerType[] = ['ZSM-WIFI-USB', 'ZSM-WIFI-EXT', 'ZSM-ETH-USB', 'ZSM-ETH-EXT'];

  // Logger upload form
  loggerUploadForm = {
    file: null as File | null,
    loggerType: '' as LoggerType | '',
    notes: ''
  };
  isUploadingLogger = false;
  loggerUploadProgress = 0;

  // Logger firmware list
  myLoggerFirmware$!: Observable<LoggerFirmwareItem[]>;
  loggerFirmwareRefresh$ = new BehaviorSubject<void>(undefined);

  // Logger wizard state
  loggerWizardStep: 'select-pattern' | 'edit-config' | 'preview' = 'select-pattern';
  loggerWizardSelections: LoggerWizardSelections = {
    pattern: null,
    originalConfig: null,
    override: {
      LATEST_SOLARMAN_LOGGER_BY_WEB: '',
      LATEST_SOLARMAN_LOGGER: '',
      LINK_SOLARMAN_LOGGER: ''
    }
  };

  // Logger available options
  availableLoggerPatterns$!: Observable<Array<{ pattern: string; id: string }>>;

  // Logger overrides
  myLoggerOverrides$!: Observable<Record<string, LoggerOverrideData>>;
  loggerOverridesRefresh$ = new BehaviorSubject<void>(undefined);

  // ========== SAFETY PROPERTIES ==========

  // Safety device model selector (must select before upload/wizard)
  selectedSafetyDeviceModel = '';

  // Safety upload forms
  safetyBinaryUploadForm = {
    file: null as File | null,
    notes: ''
  };
  safetyCustomUploadForm = {
    file: null as File | null,
    notes: ''
  };
  isUploadingSafetyBinary = false;
  isUploadingSafetyCustom = false;
  safetyBinaryUploadProgress = 0;
  safetyCustomUploadProgress = 0;
  extractedSafetyBinaryMetadata: { buildDate: string; firmwareName: string; firmwareVersion: string } | null = null;
  extractedSafetyCustomMetadata: { countryCode: number; regionCode: number; version: number; overrideKey: string } | null = null;

  // Safety firmware lists
  mySafetyBinaries$!: Observable<SafetyBinaryItem[]>;
  mySafetyCustom$!: Observable<SafetyCustomItem[]>;
  safetyBinariesRefresh$ = new BehaviorSubject<void>(undefined);
  safetyCustomRefresh$ = new BehaviorSubject<void>(undefined);

  // Safety wizard state (2 steps: select-files → preview)
  safetyWizardStep: 'select-files' | 'preview' = 'select-files';
  safetyWizardSelections = {
    pattern: '' as string,
    selectedBinaryPath: '' as string,
    selectedCustomPaths: [] as string[]
  };

  // Safety overrides
  mySafetyOverrides$!: Observable<SafetyOverrides>;
  safetyOverridesRefresh$ = new BehaviorSubject<void>(undefined);

  // Available safety patterns (from production config)
  availableSafetyPatterns$!: Observable<Array<{ pattern: string; id: string }>>;

  constructor(
    private fb: FormBuilder,
    private firmwareStorage: FirmwareTestStorageService,
    private testConfig: TestConfigurationService,
    private firmwareConfigService: FirmwareConfigService,
    private toastService: ToastService,
    readonly el: ElementRef,
    readonly renderer: Renderer2
  ) {
    this.uploadForm = this.fb.group({
      deviceModel: ['', Validators.required],
      moduleType: [{ value: '', disabled: true }, Validators.required],
      chipId: [{ value: '', disabled: true }, Validators.required],
      version: [{ value: '', disabled: true }, Validators.required],
      notes: ['']
    });
  }

  async ngOnInit() {
    // Initialize stepper orientation
    this.updateStepperOrientation();

    // Initialize current mode from Firestore
    const isTestMode = await this.firmwareConfigService.isTestModeEnabled();
    this.currentMode = isTestMode ? 'test' : 'production';

    // Subscribe to test mode changes for reactive updates
    this.firmwareConfigService.observeTestMode()
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        this.currentMode = enabled ? 'test' : 'production';
      });

    this.loadData();
    this.loadDeviceModels();
    this.initWizard();
    this.initLoggerWizard();
    this.initSafetyWizard();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ========== STEPPER ==========

  onStepChange(event: any) {
    this.selectedStepIndex = event.selectedIndex;
  }

  // ========== MODE TOGGLE ==========

  async toggleMode(mode: 'production' | 'test') {
    try {
      await this.firmwareConfigService.setTestMode(mode === 'test');
      // currentMode will be updated automatically via observeTestMode subscription

      if (mode === 'test') {
        this.toastService.simple(`⚠️ ${this.labels.testModeActivated}`, 3000);
      } else {
        this.toastService.simple(`✅ ${this.labels.productionModeRestored}`, 3000);
      }
    } catch (error) {
      console.error('[TestConfigEditor] Failed to toggle mode:', error);
      this.toastService.simple(this.labels.errorChangingMode, 3000);
    }
  }

  // ========== TAB 1: UPLOAD ==========

  async onFileSelected(file: File) {
    if (!file) return;

    // Validate file extension
    if (!file.name.endsWith('.bin')) {
      this.toastService.simple(this.labels.selectBinFile, 3000);
      return;
    }

    this.selectedFile = file;
    this.extractedMetadata = null;
    this.extractedFirmwareEffect = 0;
    this.firmwareEffectLabel = '';
    this.moduleTypeFromEffect = '';
    this.uploadForm.patchValue({ version: '', chipId: '', moduleType: '' });

    // Extract metadata from binary
    try {
      const arrayBuffer = await this.selectedFile.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const extracted = SgpInteraction.ExtractMetadataFromFirmware(uint8Array);

      // Convert firmwareEffect Uint8Array to number
      this.extractedFirmwareEffect = extracted.firmwareEffect[0]; // First byte is the value

      // Get human-readable label
      this.firmwareEffectLabel = FirmwareEffectLabels[this.extractedFirmwareEffect as FirmwareEffect] || `Unknown (${this.extractedFirmwareEffect})`;

      // Get module type for configuration
      this.moduleTypeFromEffect = FirmwareEffectToModuleType[this.extractedFirmwareEffect as FirmwareEffect] || '';

      // Display extracted metadata
      this.extractedMetadata = {
        firmwareName: extracted.firmwareName,
        firmwareVersion: extracted.firmwareVersion,
        firmwareSize: this.uint8ArrayToHex(extracted.firmwareSize),
        firmwareCRC32: this.uint8ArrayToHex(extracted.firmwareCRC32),
        firmwareEffect: this.extractedFirmwareEffect,
        firmwareEffectLabel: this.firmwareEffectLabel,
        chipModel: extracted.chipModel || 'N/A'
      };

      // Auto-fill form
      this.uploadForm.patchValue({
        version: extracted.firmwareVersion,
        chipId: extracted.chipModel || '',
        moduleType: this.moduleTypeFromEffect
      });
    } catch (error: any) {
      console.error('Failed to extract metadata', error);
      this.toastService.simple(`${this.labels.errorExtractingMetadata}: ${error?.message || 'Unknown error'}`, 5000);
    }
  }

  onFileCleared() {
    this.selectedFile = null;
    this.extractedMetadata = null;
    this.extractedFirmwareEffect = 0;
    this.firmwareEffectLabel = '';
    this.moduleTypeFromEffect = '';
    this.uploadForm.patchValue({ version: '', chipId: '', moduleType: '' });
  }

  uploadFirmware = async () => {
    if (!this.selectedFile || !this.uploadForm.valid || this.isUploading) return;

    const formValues = this.uploadForm.getRawValue(); // Get all values including disabled fields

    // Validate that version and moduleType are not empty
    if (!formValues.version?.trim() || !formValues.moduleType?.trim()) {
      this.toastService.simple(this.labels.versionAndModuleTypeRequired, 5000);
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;

    try {
      const metadata: InverterFirmwareMetadata = {
        deviceModel: formValues.deviceModel,
        moduleType: formValues.moduleType,
        chipId: formValues.chipId,
        version: formValues.version,
        notes: formValues.notes || ''
      };

      await this.firmwareStorage.uploadTestFirmware(
        this.selectedFile,
        metadata,
        (progress) => {
          this.uploadProgress = Math.round(progress);
        }
      );

      this.toastService.simple(this.labels.firmwareUploadedSuccessfully, 3000);
      this.resetUploadForm();
      this.firmwareRefresh$.next();
    } catch (error: any) {
      console.error('Upload error:', error);
      this.toastService.simple(`${this.labels.errorUploading}: ${error?.message || 'Unknown error'}`, 5000);
    } finally {
      this.isUploading = false;
      this.uploadProgress = 0;
    }
  }

  resetUploadForm = () => {
    this.selectedFile = null;
    this.extractedMetadata = null;
    this.extractedFirmwareEffect = 0;
    this.firmwareEffectLabel = '';
    this.moduleTypeFromEffect = '';
    this.uploadForm.reset();
    this.uploadProgress = 0;
  }

  // ========== TAB 3: WIZARD ==========

  initWizard() {
    this.wizardStep = 'select-pattern';
    this.isLoadingCompatibleFirmware = false;
    this.wizardFilterDeviceModel = '';
    this.wizardSelections = {
      pattern: null,
      patternConfig: null,
      moduleType: null,
      chipId: null,
      originalChipData: null,
      selectedFirmware: null
    };

    this.availablePatterns$ = this.productionConfig$.pipe(
      map(config => Object.keys(config)
        .filter(key => (config[key] as any).DEVICE_MODEL_ID === 'INVERTER')
        .map(pattern => ({
          pattern,
          id: (config[pattern] as any).ID || pattern
        }))
      )
    );
  }

  async selectPattern(pattern: string) {
    const config = await this.productionConfig$.pipe(first()).toPromise();
    if (!config) return;

    this.wizardSelections.pattern = pattern;
    this.wizardSelections.patternConfig = config[pattern] as PatternConfig;
    this.wizardStep = 'select-module';

    const modules: string[] = [];
    const patternConfig = this.wizardSelections.patternConfig;

    ['ARM', 'DSPM', 'DSPS', 'AFCI', 'SAFETY'].forEach(module => {
      if (patternConfig[module]) modules.push(module);
    });

    if (patternConfig.BATTERY) {
      if (patternConfig.BATTERY.BMS) modules.push('BATTERY.BMS');
      if (patternConfig.BATTERY.PCU) modules.push('BATTERY.PCU');
      if (patternConfig.BATTERY.BDU) modules.push('BATTERY.BDU');
    }

    this.availableModules$ = of(modules);
  }

  selectModule(moduleType: string) {
    this.wizardSelections.moduleType = moduleType as AllModuleTypes;
    this.wizardStep = 'select-chip';

    let chips: ChipData[] = [];

    if (moduleType.startsWith('BATTERY.')) {
      const batteryModule = moduleType.replace('BATTERY.', '');
      chips = this.wizardSelections.patternConfig?.BATTERY?.[batteryModule] || [];
    } else {
      chips = this.wizardSelections.patternConfig?.[moduleType] || [];
    }

    this.availableChips$ = of(Array.isArray(chips) ? chips : []);
  }

  selectChip(chipData: ChipData) {
    this.wizardSelections.chipId = chipData.CHIP;
    this.wizardSelections.originalChipData = chipData;
    this.wizardStep = 'select-firmware';
    this.isLoadingCompatibleFirmware = true;

    // Use ID field directly from patternConfig instead of extracting from regex pattern
    const deviceModel = this.wizardSelections.patternConfig?.ID || '';

    this.compatibleFirmware$ = from(this.firmwareStorage.listTestFirmware({
      deviceModel,
      moduleType: this.wizardSelections.moduleType!
    })).pipe(
      tap(() => this.isLoadingCompatibleFirmware = false)
    );
  }

  selectFirmware(firmware: InverterFirmwareItem) {
    this.wizardSelections.selectedFirmware = firmware;
    this.wizardStep = 'preview';
  }

  confirmAndSaveOverride = async () => {
    const fw = this.wizardSelections.selectedFirmware!;
    const original = this.wizardSelections.originalChipData!;
    const moduleType = this.wizardSelections.moduleType!;

    // Find the correct chip entry based on firmware version prefix.
    // The firmware version prefix (first non-zero char) must match the chip's MIN_VERSION prefix,
    // otherwise compareInverterFWModuleVersions returns NOT_COMPARABLE at runtime.
    let matchedChip = original;
    const isInverterModule = ['ARM', 'DSPM', 'DSPS', 'AFCI'].includes(moduleType);

    if (isInverterModule && this.wizardSelections.patternConfig) {
      const fwVersionPrefix = fw.metadata.version.replace(/^0+/, '').charAt(0).toUpperCase();
      const chips: ChipData[] = this.wizardSelections.patternConfig[moduleType] || [];

      const correctChip = chips.find(chip => {
        const minVersionPrefix = chip.MIN_VERSION.replace(/^0+/, '').charAt(0).toUpperCase();
        return minVersionPrefix === fwVersionPrefix;
      });

      if (correctChip && correctChip.CHIP !== original.CHIP) {
        console.log(`[TestConfigEditor] Auto-corrected chip: selected ${original.CHIP} (MIN=${original.MIN_VERSION}) → matched ${correctChip.CHIP} (MIN=${correctChip.MIN_VERSION}) based on firmware prefix '${fwVersionPrefix}'`);
        matchedChip = correctChip;
      }
    }

    const chipData: ChipData = {
      CHIP: matchedChip.CHIP,
      LATEST_VERSION: fw.metadata.version,
      LINK: fw.downloadURL,
      MIN_VERSION: matchedChip.MIN_VERSION,
      MODEL: matchedChip.MODEL
    };

    try {
      await this.testConfig.saveOverride(
        this.wizardSelections.pattern!,
        this.wizardSelections.moduleType!,
        chipData
      );

      this.toastService.simple(`✅ ${this.labels.overrideSavedSuccessfully}`, 3000);
      this.overridesRefresh$.next();
      this.initWizard();
    } catch (error: any) {
      console.error('Save override error:', error);
      this.toastService.simple(`${this.labels.errorSavingOverride}: ${error?.message || 'Unknown error'}`, 5000);
    }
  }

  wizardBack = () => {
    const steps: typeof this.wizardStep[] = [
      'select-pattern', 'select-module', 'select-chip', 'select-firmware', 'preview'
    ];
    const currentIndex = steps.indexOf(this.wizardStep);
    if (currentIndex > 0) {
      this.wizardStep = steps[currentIndex - 1];
    }
  }

  async removeOverride(pattern: string, moduleType: string, chipId: string) {
    const confirmed = confirm(`${this.labels.confirmRemoveOverride} ${pattern} → ${moduleType}.${chipId}?`);

    if (confirmed) {
      try {
        await this.testConfig.removeOverride(pattern, moduleType, chipId);
        this.toastService.simple(this.labels.overrideRemoved, 3000);
        this.overridesRefresh$.next();
        this.configRefresh$.next();
      } catch (error: any) {
        console.error('Remove override error:', error);
        this.toastService.simple(`${this.labels.errorRemoving}: ${error?.message || 'Unknown error'}`, 5000);
      }
    }
  }

  removeOverridePattern = async (pattern: string) => {
    const confirmed = confirm(`${this.labels.confirmRemoveAllOverrides} "${pattern}"?`);

    if (confirmed) {
      try {
        await this.testConfig.removeOverridePattern(pattern);
        this.toastService.simple(this.labels.patternOverrideRemoved, 3000);
        this.overridesRefresh$.next();
        this.configRefresh$.next();
      } catch (error: any) {
        console.error('Remove pattern override error:', error);
        this.toastService.simple(`${this.labels.errorRemovingPattern}: ${error?.message || 'Unknown error'}`, 5000);
      }
    }
  }

  clearAllOverrides = async () => {
    const confirmed = confirm(`${this.labels.confirmRemoveAllOverridesQuestion}`);

    if (confirmed) {
      try {
        await this.testConfig.clearAllOverrides();
        this.toastService.simple(this.labels.allOverridesRemoved, 3000);
        this.overridesRefresh$.next();
        this.configRefresh$.next();
      } catch (error: any) {
        console.error('Clear overrides error:', error);
        this.toastService.simple(`${this.labels.error}: ${error?.message || 'Unknown error'}`, 5000);
      }
    }
  }

  // ========== UTILITIES ==========

  loadDeviceModels() {
    this.availableDeviceModels$ = this.productionConfig$.pipe(
      map(config => {
        const models: { value: string; label: string }[] = [];

        for (const pattern in config) {
          const patternConfig = config[pattern] as any;

          // Filter only INVERTER devices
          if (patternConfig.DEVICE_MODEL_ID === 'INVERTER' && patternConfig.ID) {
            // Use the ID field directly
            const deviceModelId = patternConfig.ID;

            if (!models.find(m => m.value === deviceModelId)) {
              models.push({
                value: deviceModelId,
                label: deviceModelId
              });
            }
          }
        }

        // Sort alphabetically
        return models.sort((a, b) => a.label.localeCompare(b.label));
      })
    );
  }

  isChipDataArray(value: any): boolean {
    return Array.isArray(value);
  }

  asChipDataArray(value: any): ChipData[] {
    return value as ChipData[];
  }

  /**
   * Check if a pattern should be shown in config viewer (INVERTER)
   * - Must be INVERTER
   * - Must have at least one module with data
   */
  shouldShowPattern(patternConfig: any): boolean {
    if (patternConfig?.DEVICE_MODEL_ID !== 'INVERTER') {
      return false;
    }

    // Check if any module has data
    const hasARM = this.hasModuleData(patternConfig.ARM);
    const hasDSPM = this.hasModuleData(patternConfig.DSPM);
    const hasDSPS = this.hasModuleData(patternConfig.DSPS);
    const hasAFCI = this.hasModuleData(patternConfig.AFCI);
    const hasSAFETY = this.hasModuleData(patternConfig.SAFETY);
    const hasBMS = this.hasModuleData(patternConfig.BATTERY?.BMS);
    const hasPCU = this.hasModuleData(patternConfig.BATTERY?.PCU);
    const hasBDU = this.hasModuleData(patternConfig.BATTERY?.BDU);

    return hasARM || hasDSPM || hasDSPS || hasAFCI || hasSAFETY || hasBMS || hasPCU || hasBDU;
  }

  /**
   * Check if a pattern is a LOGGER device
   */
  isLoggerPattern(patternConfig: any): boolean {
    const validLoggerTypes: LoggerType[] = ['ZSM-WIFI-USB', 'ZSM-WIFI-EXT', 'ZSM-ETH-USB', 'ZSM-ETH-EXT'];
    return validLoggerTypes.includes(patternConfig?.DEVICE_MODEL_ID);
  }

  /**
   * Check if a pattern has SAFETY configuration
   */
  hasSafetyConfig(patternConfig: any): boolean {
    return patternConfig?.DEVICE_MODEL_ID === 'INVERTER' &&
      (Array.isArray(patternConfig?.SAFETY) && patternConfig.SAFETY.length > 0 ||
       Array.isArray(patternConfig?.SAFETY_CUSTOM) && patternConfig.SAFETY_CUSTOM.length > 0);
  }

  /**
   * Check if pattern matches device model filter
   */
  matchesDeviceModelFilter(patternKey: string, filterModel: string): boolean {
    if (!filterModel) return true;
    return patternKey.startsWith(filterModel);
  }

  /**
   * Check if a module has data (non-empty array)
   */
  hasModuleData(moduleArray: any): boolean {
    return Array.isArray(moduleArray) && moduleArray.length > 0;
  }

  /**
   * Filter firmware list by device model
   */
  filterFirmwareByDeviceModel(firmware: InverterFirmwareItem[], filter: string): InverterFirmwareItem[] {
    if (!filter) return firmware;
    return firmware.filter(fw => fw.metadata.deviceModel === filter);
  }

  /**
   * Check if a pattern config matches the device model filter
   */
  shouldShowPatternWithFilter(patternConfig: any, filter: string): boolean {
    if (!this.shouldShowPattern(patternConfig)) return false;
    if (!filter) return true;
    return patternConfig.ID === filter;
  }

  /**
   * Filter pattern list by device model
   */
  filterPatternsByDeviceModel(patterns: Array<{ pattern: string; id: string }>, filter: string): Array<{ pattern: string; id: string }> {
    if (!filter) return patterns;
    return patterns.filter(p => p.id === filter);
  }

  private uint8ArrayToHex(arr: Uint8Array): string {
    return Array.from(arr)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // ========== LOGGER METHODS ==========

  // TAB 1: Logger Upload

  onLoggerFileSelected(file: File) {
    if (!file) return;

    // Validate file extension
    if (!file.name.endsWith('.bin')) {
      this.toastService.simple(this.labels.selectBinFile, 3000);
      return;
    }

    this.loggerUploadForm.file = file;
  }

  onLoggerFileCleared() {
    this.loggerUploadForm.file = null;
  }

  uploadLoggerFirmware = async () => {
    if (!this.loggerUploadForm.file || !this.loggerUploadForm.loggerType || this.isUploadingLogger) {
      this.toastService.simple(this.labels.fileAndLoggerTypeRequired, 3000);
      return;
    }

    this.isUploadingLogger = true;
    this.loggerUploadProgress = 0;

    try {
      await this.firmwareStorage.uploadLoggerFirmware(
        this.loggerUploadForm.file,
        this.loggerUploadForm.loggerType,
        { notes: this.loggerUploadForm.notes },
        (progress) => {
          this.loggerUploadProgress = Math.round(progress);
        }
      );

      this.toastService.simple(this.labels.loggerFirmwareUploadedSuccessfully, 3000);
      this.resetLoggerUploadForm();
      this.loggerFirmwareRefresh$.next();
    } catch (error: any) {
      console.error('Logger upload error:', error);
      this.toastService.simple(`${this.labels.errorUploading}: ${error?.message || 'Unknown error'}`, 5000);
    } finally {
      this.isUploadingLogger = false;
      this.loggerUploadProgress = 0;
    }
  }

  resetLoggerUploadForm = () => {
    this.loggerUploadForm = {
      file: null,
      loggerType: '',
      notes: ''
    };
    this.loggerUploadProgress = 0;
  }

  // TAB 3: Logger Override Wizard

  initLoggerWizard() {
    this.loggerWizardStep = 'select-pattern';
    this.loggerWizardSelections = {
      pattern: null,
      originalConfig: null,
      override: {
        LATEST_SOLARMAN_LOGGER_BY_WEB: '',
        LATEST_SOLARMAN_LOGGER: '',
        LINK_SOLARMAN_LOGGER: ''
      }
    };

    // Load available logger patterns
    this.availableLoggerPatterns$ = this.productionConfig$.pipe(
      map(config => {
        const validLoggerTypes: LoggerType[] = ['ZSM-WIFI-USB', 'ZSM-WIFI-EXT', 'ZSM-ETH-USB', 'ZSM-ETH-EXT'];
        return Object.keys(config)
          .filter(pattern => validLoggerTypes.includes((config[pattern] as any).DEVICE_MODEL_ID))
          .map(pattern => ({
            pattern,
            id: (config[pattern] as any).ID || pattern
          }));
      })
    );
  }

  async selectLoggerPattern(pattern: string) {
    const config = await this.productionConfig$.pipe(first()).toPromise();
    if (!config) return;

    const loggerConfig = config[pattern] as LoggerConfig;
    this.loggerWizardSelections.pattern = pattern;
    this.loggerWizardSelections.originalConfig = loggerConfig;

    // Pre-fill override with original values
    this.loggerWizardSelections.override = {
      LATEST_SOLARMAN_LOGGER_BY_WEB: loggerConfig.LATEST_SOLARMAN_LOGGER_BY_WEB || '',
      LATEST_SOLARMAN_LOGGER: loggerConfig.LATEST_SOLARMAN_LOGGER || '',
      LINK_SOLARMAN_LOGGER: '' // Empty, user must select
    };

    this.loggerWizardStep = 'edit-config';
  }

  isLoggerFormValid(): boolean {
    return !!(
      this.loggerWizardSelections.override.LATEST_SOLARMAN_LOGGER_BY_WEB?.trim() &&
      this.loggerWizardSelections.override.LATEST_SOLARMAN_LOGGER?.trim() &&
      this.loggerWizardSelections.override.LINK_SOLARMAN_LOGGER?.trim()
    );
  }

  loggerWizardNext = () => {
    if (!this.isLoggerFormValid()) {
      this.toastService.simple(this.labels.allFieldsRequired, 3000);
      return;
    }
    this.loggerWizardStep = 'preview';
  }

  loggerWizardBack = () => {
    const steps: typeof this.loggerWizardStep[] = ['select-pattern', 'edit-config', 'preview'];
    const currentIndex = steps.indexOf(this.loggerWizardStep);
    if (currentIndex > 0) {
      this.loggerWizardStep = steps[currentIndex - 1];
    }
  }

  confirmAndSaveLoggerOverride = async () => {
    try {
      const overrideData: LoggerOverrideData = {
        DEVICE_MODEL_ID: this.loggerWizardSelections.originalConfig!.DEVICE_MODEL_ID as LoggerType,
        LATEST_SOLARMAN_LOGGER_BY_WEB: this.loggerWizardSelections.override.LATEST_SOLARMAN_LOGGER_BY_WEB,
        LATEST_SOLARMAN_LOGGER: this.loggerWizardSelections.override.LATEST_SOLARMAN_LOGGER,
        LINK_SOLARMAN_LOGGER: this.loggerWizardSelections.override.LINK_SOLARMAN_LOGGER
      };

      await this.testConfig.saveLoggerOverride(
        this.loggerWizardSelections.pattern!,
        overrideData
      );

      this.toastService.simple(`✅ ${this.labels.loggerOverrideSavedSuccessfully}`, 3000);
      this.resetLoggerWizard();
      this.loggerOverridesRefresh$.next();
    } catch (error: any) {
      console.error('Save logger override error:', error);
      this.toastService.simple(`${this.labels.errorSaving}: ${error?.message || 'Unknown error'}`, 5000);
    }
  }

  resetLoggerWizard() {
    this.initLoggerWizard();
  }

  async removeLoggerOverride(pattern: string) {
    const confirmed = confirm(`${this.labels.confirmRemoveLoggerOverride} ${pattern}?`);

    if (confirmed) {
      try {
        await this.testConfig.removeLoggerOverride(pattern);
        this.toastService.simple(this.labels.overrideRemoved, 3000);
        this.loggerOverridesRefresh$.next();
      } catch (error: any) {
        console.error('Remove logger override error:', error);
        this.toastService.simple(`${this.labels.errorRemoving}: ${error?.message || 'Unknown error'}`, 5000);
      }
    }
  }

  getSelectedLoggerFirmwareDetails(): LoggerFirmwareItem | undefined {
    const selectedPath = this.loggerWizardSelections.override.LINK_SOLARMAN_LOGGER;
    if (!selectedPath) return undefined;

    const path = selectedPath.replace('firebase://', '');

    let result: LoggerFirmwareItem | undefined;
    this.myLoggerFirmware$.pipe(first()).subscribe(firmwares => {
      result = firmwares.find(fw => fw.path === path);
    });

    return result;
  }

  // ========== SAFETY METHODS ==========

  async onSafetyBinaryFileSelected(file: File) {
    if (!file) return;

    if (!file.name.endsWith('.bin')) {
      this.toastService.simple(this.labels.selectBinFile, 3000);
      return;
    }

    this.safetyBinaryUploadForm.file = file;
    this.extractedSafetyBinaryMetadata = null;

    // Extract metadata from binary
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const extracted = SgpInteraction.ExtractMetadataFromFirmware(uint8Array);

      this.extractedSafetyBinaryMetadata = {
        buildDate: extracted.buildDate || '',
        firmwareName: extracted.firmwareName,
        firmwareVersion: extracted.firmwareVersion
      };
    } catch (error) {
      console.error('Error extracting safety binary metadata:', error);
      this.toastService.simple(this.labels.errorExtractingMetadata, 3000);
    }
  }

  async onSafetyCustomFileSelected(file: File) {
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      this.toastService.simple('Please select a .txt file', 3000);
      return;
    }

    this.safetyCustomUploadForm.file = file;
    this.extractedSafetyCustomMetadata = null;

    // Parse first line to extract country/region/version
    try {
      const content = await file.text();
      const parsed = parseSafetyCustomHeader(content);

      if (parsed) {
        this.extractedSafetyCustomMetadata = {
          ...parsed,
          overrideKey: buildSafetyOverrideKey(parsed)
        };
      } else {
        this.toastService.simple('Invalid header format. Expected: COUNTRY&Region&Ver:{cc},{rc},{ver}', 5000);
      }
    } catch (error) {
      console.error('Error parsing safety custom file:', error);
      this.toastService.simple(this.labels.errorExtractingMetadata, 3000);
    }
  }

  uploadSafetyBinary = async () => {
    if (!this.safetyBinaryUploadForm.file || !this.selectedSafetyDeviceModel) {
      this.toastService.simple(this.labels.selectDeviceModelFirst, 3000);
      return;
    }

    this.isUploadingSafetyBinary = true;
    this.safetyBinaryUploadProgress = 0;

    try {
      await this.firmwareStorage.uploadSafetyBinary(
        this.safetyBinaryUploadForm.file,
        {
          deviceModel: this.selectedSafetyDeviceModel,
          notes: this.safetyBinaryUploadForm.notes
        },
        (progress) => this.safetyBinaryUploadProgress = progress
      );

      this.toastService.simple(`✅ ${this.labels.firmwareUploadedSuccessfully}`, 3000);
      this.resetSafetyBinaryUpload();
      this.safetyBinariesRefresh$.next();
    } catch (error: any) {
      console.error('Safety binary upload error:', error);
      this.toastService.simple(`${this.labels.errorUploading}: ${error?.message || 'Unknown error'}`, 5000);
    } finally {
      this.isUploadingSafetyBinary = false;
    }
  };

  uploadSafetyCustom = async () => {
    if (!this.safetyCustomUploadForm.file || !this.selectedSafetyDeviceModel) {
      this.toastService.simple(this.labels.selectDeviceModelFirst, 3000);
      return;
    }

    this.isUploadingSafetyCustom = true;
    this.safetyCustomUploadProgress = 0;

    try {
      await this.firmwareStorage.uploadSafetyCustom(
        this.safetyCustomUploadForm.file,
        {
          deviceModel: this.selectedSafetyDeviceModel,
          notes: this.safetyCustomUploadForm.notes
        },
        (progress) => this.safetyCustomUploadProgress = progress
      );

      this.toastService.simple(`✅ ${this.labels.firmwareUploadedSuccessfully}`, 3000);
      this.resetSafetyCustomUpload();
      this.safetyCustomRefresh$.next();
    } catch (error: any) {
      console.error('Safety custom upload error:', error);
      this.toastService.simple(`${this.labels.errorUploading}: ${error?.message || 'Unknown error'}`, 5000);
    } finally {
      this.isUploadingSafetyCustom = false;
    }
  };

  resetSafetyBinaryUpload = () => {
    this.safetyBinaryUploadForm = { file: null, notes: '' };
    this.extractedSafetyBinaryMetadata = null;
    this.safetyBinaryUploadProgress = 0;
  };

  resetSafetyCustomUpload = () => {
    this.safetyCustomUploadForm = { file: null, notes: '' };
    this.extractedSafetyCustomMetadata = null;
    this.safetyCustomUploadProgress = 0;
  };

  // Safety Wizard Methods
  initSafetyWizard() {
    // Get patterns with SAFETY module from production config
    this.availableSafetyPatterns$ = this.productionConfig$.pipe(
      map(config => {
        const patterns: Array<{ pattern: string; id: string }> = [];
        for (const [pattern, patternConfig] of Object.entries(config)) {
          const cfg = patternConfig as PatternConfig;
          // Only include patterns with SAFETY array
          if (cfg.DEVICE_MODEL_ID === 'INVERTER' && (cfg as any).SAFETY) {
            patterns.push({
              pattern,
              id: cfg.ID || pattern
            });
          }
        }
        return patterns;
      })
    );
  }

  onSafetyDeviceModelChange() {
    // Reset wizard when device model changes
    this.resetSafetyWizard();
    // Refresh file lists for selected device model
    this.safetyBinariesRefresh$.next();
    this.safetyCustomRefresh$.next();
  }

  goToSafetyPreview = () => {
    // Validate: at least one file must be selected
    if (!this.safetyWizardSelections.selectedBinaryPath && this.safetyWizardSelections.selectedCustomPaths.length === 0) {
      this.toastService.simple('Select at least one file (binary or custom)', 3000);
      return;
    }

    // Auto-deduce pattern from device model (first matching pattern)
    this.availableSafetyPatterns$.pipe(first()).subscribe(patterns => {
      const matchingPattern = patterns.find(p => p.pattern.startsWith(this.selectedSafetyDeviceModel || ''));
      if (matchingPattern) {
        this.safetyWizardSelections.pattern = matchingPattern.pattern;
      } else if (patterns.length > 0) {
        this.safetyWizardSelections.pattern = patterns[0].pattern;
      }
    });

    this.safetyWizardStep = 'preview';
  };

  toggleSafetyCustomPath = (path: string) => {
    const index = this.safetyWizardSelections.selectedCustomPaths.indexOf(path);
    if (index >= 0) {
      this.safetyWizardSelections.selectedCustomPaths.splice(index, 1);
    } else {
      this.safetyWizardSelections.selectedCustomPaths.push(path);
    }
  };

  saveSafetyOverride = async () => {
    if (!this.safetyWizardSelections.pattern) {
      this.toastService.simple(this.labels.allFieldsRequired, 3000);
      return;
    }

    try {
      const overrideData: SafetyOverrideData = {
        customLinks: this.safetyWizardSelections.selectedCustomPaths
      };

      if (this.safetyWizardSelections.selectedBinaryPath) {
        overrideData.binaryLink = this.safetyWizardSelections.selectedBinaryPath;
      }

      await this.testConfig.saveSafetyOverride(
        this.safetyWizardSelections.pattern,
        overrideData
      );

      this.toastService.simple(`✅ ${this.labels.safetyOverrideSavedSuccessfully}`, 3000);
      this.resetSafetyWizard();
      this.safetyOverridesRefresh$.next();
    } catch (error: any) {
      console.error('Save safety override error:', error);
      this.toastService.simple(`${this.labels.errorSaving}: ${error?.message || 'Unknown error'}`, 5000);
    }
  };

  resetSafetyWizard = () => {
    this.safetyWizardStep = 'select-files';
    this.safetyWizardSelections = {
      pattern: '',
      selectedBinaryPath: '',
      selectedCustomPaths: []
    };
  };

  removeSafetyOverride = async (pattern: string) => {
    const confirmed = confirm(`${this.labels.confirmRemoveSafetyOverride} ${pattern}?`);

    if (confirmed) {
      try {
        await this.testConfig.removeSafetyOverride(pattern);
        this.toastService.simple(this.labels.overrideRemoved, 3000);
        this.safetyOverridesRefresh$.next();
      } catch (error: any) {
        console.error('Remove safety override error:', error);
        this.toastService.simple(`${this.labels.errorRemoving}: ${error?.message || 'Unknown error'}`, 5000);
      }
    }
  };

  getSelectedSafetyBinaryDetails(): SafetyBinaryItem | undefined {
    if (!this.safetyWizardSelections.selectedBinaryPath) return undefined;

    let result: SafetyBinaryItem | undefined;
    this.mySafetyBinaries$.pipe(first()).subscribe(binaries => {
      result = binaries.find(b => b.path === this.safetyWizardSelections.selectedBinaryPath);
    });

    return result;
  }

  private loadData() {
    // Reload config when configRefresh$ emits (so wizard shows updated state)
    this.productionConfig$ = this.configRefresh$.pipe(
      tap(() => this.isLoadingConfig = true),
      switchMap(() => from(this.firmwareConfigService.getFirmwareConfig())),
      tap(() => this.isLoadingConfig = false),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.myTestFirmware$ = this.firmwareRefresh$.pipe(
      tap(() => this.isLoadingFirmware = true),
      switchMap(() => from(this.firmwareStorage.listTestFirmware())),
      tap(() => this.isLoadingFirmware = false)
    );

    this.myOverrides$ = this.overridesRefresh$.pipe(
      tap(() => this.isLoadingOverrides = true),
      switchMap(() => from(this.testConfig.loadMyOverrides())),
      tap(() => this.isLoadingOverrides = false)
    );

    // Logger observables
    this.myLoggerFirmware$ = this.loggerFirmwareRefresh$.pipe(
      tap(() => this.isLoadingLoggerFirmware = true),
      switchMap(() => from(this.firmwareStorage.listLoggerFirmware())),
      tap(() => this.isLoadingLoggerFirmware = false)
    );

    this.myLoggerOverrides$ = this.loggerOverridesRefresh$.pipe(
      tap(() => this.isLoadingLoggerOverrides = true),
      switchMap(() => from(this.testConfig.getLoggerOverrides())),
      tap(() => this.isLoadingLoggerOverrides = false)
    );

    // Safety observables (loading state handled by async pipe + else template in HTML)
    this.mySafetyBinaries$ = this.safetyBinariesRefresh$.pipe(
      switchMap(() => from(this.firmwareStorage.listSafetyBinaries(
        this.selectedSafetyDeviceModel ? { deviceModel: this.selectedSafetyDeviceModel } : undefined
      )).pipe(
        catchError(() => of([]))
      )),
      shareReplay(1)
    );

    this.mySafetyCustom$ = this.safetyCustomRefresh$.pipe(
      switchMap(() => from(this.firmwareStorage.listSafetyCustom(
        this.selectedSafetyDeviceModel ? { deviceModel: this.selectedSafetyDeviceModel } : undefined
      )).pipe(
        catchError(() => of([]))
      )),
      shareReplay(1)
    );

    this.mySafetyOverrides$ = this.safetyOverridesRefresh$.pipe(
      switchMap(() => from(this.testConfig.getSafetyOverrides()).pipe(
        catchError(() => of({} as SafetyOverrides))
      )),
      shareReplay(1)
    );
  }
}
